import React, { useCallback } from 'react';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Alert,
  TextareaField,
  SelectField,
  Label,
} from '@printdeal/ui-library';
import { AlternativeUpload, IAlternativeUpload } from '@printdeal/alternative-upload';

import { FeedbackFormValues, dispatchDataLayerEventOnFieldInteraction } from './helpers/conversation-loop';
import { typeOfFeedbacks, validationMessages, generalMessages } from './helpers/message';

interface FeedbackModalContentProps {
  form: UseFormReturn<FeedbackFormValues>;
  mutation: {
    isSuccess: boolean;
    isError: boolean;
    status: string;
  };
  onSubmit: (values: FeedbackFormValues) => void;
}

const FeedbackForm = ({
  form,
  mutation,
  onSubmit,
}: FeedbackModalContentProps) => {
  const intl = useIntl();
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = form;

  const handleFieldInteraction = useCallback((fieldName: string) => {
    dispatchDataLayerEventOnFieldInteraction(fieldName);
  }, []);

  const alternativeUploadHandler = useCallback(async (uploads: IAlternativeUpload[]) => {
    const attachments = uploads.map((upload: IAlternativeUpload) => ({
      id: upload.id,
      fileType: upload.fileType,
      fileSize: upload.fileSize,
      uploadTitle: upload.uploadTitle,
      uploadTimestamp: upload.uploadTimestamp || new Date().toISOString(),
      urls: {
        originalUrl: upload.urls?.originalUrl || '',
      },
    }));

    await setValue('attachments', attachments);
  }, [setValue]);

  const handleFormSubmit = useCallback((values: FeedbackFormValues) => {
    const pageUrl = window.location.href;
    onSubmit({ ...values, pageUrl });
  }, [onSubmit]);

  return (
    <>
      {mutation.status !== 'success' && (
        <p className="pd-mb-4">
          {intl.formatMessage(generalMessages.feedbackmodalIntro)}
        </p>
      )}
      {mutation.isSuccess && (
        <Alert variant="success">
          <FormattedMessage
            id="general.feedback.modal.success"
            defaultMessage="Thanks for Sharing the feedback"
          />
        </Alert>
      )}
      {mutation.isError && (
        <Alert variant="error" className="pd-mb-4">
          <FormattedMessage
            id="general.feedback.modal.error"
            defaultMessage="Something went wrong while sending the feedback. Please try again."
          />
        </Alert>
      )}
      {mutation.status !== 'success' && (
        <FormProvider {...form}>
          <form
            onSubmit={handleSubmit(handleFormSubmit)}
            method="POST"
            className="pd-grid pd-gap-4"
            id="feedbackmodalForm"
          >
            <SelectField
              id="feedbackType"
              defaultValue={undefined}
              label={intl.formatMessage({
                id: 'general.feedback.modal.feedbacktype.label',
                defaultMessage: 'Type of feedback',
              })}
              {...register('feedbackType')}
              required
              description={<FormattedMessage id="general.feedback.modal.feedbacktype.chooseOptionLabel" defaultMessage="Choose the Category that best fits your feedback" />}
              onChange={() => handleFieldInteraction('feedbackType')}
            >
              <SelectField.Option value="">
                <FormattedMessage id="general.feedback.modal.feedbacktype.chooseOption" defaultMessage="Choose an option" />
              </SelectField.Option>
              {Object.values(typeOfFeedbacks).map((typeOfFeedback) => (
                <SelectField.Option value={typeOfFeedback.defaultMessage} key={typeOfFeedback.id}>
                  {intl.formatMessage(typeOfFeedback)}
                </SelectField.Option>
              ))}
            </SelectField>

            <TextareaField
              id="body"
              label={intl.formatMessage({
                id: 'general.feedback.modal.body.label',
                defaultMessage: 'Your feedback',
              })}
              validation={errors.body ? 'error' : undefined}
              validationMessage={
                errors.body && intl.formatMessage(validationMessages['feedbackmodal.error.body'])
              }
              {...register('body')}
              rows={5}
              required
              description={<FormattedMessage id="general.feedback.modal.body.feedbackTextLabel" defaultMessage="Describe Your feedback as clearly as possible" />}
              onBlur={() => handleFieldInteraction('body')}
            />
            <Label>
              <FormattedMessage
                id="general.feedback.modal.attachments.title.heading"
                defaultMessage="Upload a file to clarify your feedback"
              />
            </Label>
            <AlternativeUpload
              id="feedback-modal"
              name="feedback-modal"
              onChange={alternativeUploadHandler}
              dataLayerData={{ location: 'feedback-modal' }}
            />
          </form>
        </FormProvider>
      )}
    </>
  );
};

export default FeedbackForm;
